import { useCallback } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { Configuration } from '../../api';
import { ConvertUtility } from '../../utils/ConvertUtility';
import { useNavigate } from 'react-router-dom';
import { isC3RetiredError } from '../../utils/DisneyAvailability';

export type ApiError = {
  statusCode: number;
  data: object | null;
};

export interface ApiResult<T> {
  success: T | null;
  error: ApiError | null;
}

export const useCallApi = () => {
  const navigate = useNavigate();
  const { convertKeysToCamel, convertKeysToSnake } = ConvertUtility();

  const callApi = useCallback(
    async function <TResponse, TParams>(params: TParams, fn: (params: TParams) => Promise<AxiosResponse>): Promise<ApiResult<TResponse>> {
      let response = undefined;
      try {
        response = await fn(convertKeysToSnake(params));
        response = convertKeysToCamel(response);
        return { success: response.data as TResponse, error: null };
      } catch (e) {
        console.error(e);
        if (e instanceof AxiosError) {
          console.log('axios error', e);
          const statusCode = e.response?.status;
          if (isC3RetiredError(statusCode, e.response?.data)) {
            navigate('/practice/unavailable', { replace: true });
            return { success: null, error: { statusCode: statusCode!, data: e.response?.data } };
          }
          if (statusCode) {
            if (statusCode === 401) {
              // TODO: ユーザー情報を持っていないので一旦はErrorページにリダイレクト
              navigate(`/error`);
            }
            if (statusCode !== 200) {
              navigate(`/error`);
            }
          }
          if (e.code === 'ERR_NETWORK') {
            navigate(`/error/network`);
          }
          return {
            success: null,
            error: { statusCode: statusCode!, data: e.response?.data },
          };
        } else {
          navigate(`/error/unknown`);
          throw e;
        }
      }
    },
    [convertKeysToCamel, convertKeysToSnake, navigate]
  );

  const defaultConfiguration = useCallback(async () => {
    const env = import.meta.env;
    const configuration = new Configuration();
    configuration.basePath = env.VITE_DIGITANE_API_URL;
    configuration.baseOptions = {
      headers: {
        'X-Application-Token': env.VITE_DIGITANE_API_KEY,
      },
      withCredentials: true,
    };
    return configuration;
  }, []);

  return { callApi, defaultConfiguration };
};
